/**
 * Client Profile Service
 * API helpers for client profile management
 */

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/**
 * Get profile readiness score and missing fields
 * @param {string} clientId 
 * @returns {Promise<{success: boolean, score: number, missing: string[]}>}
 */
/**
 * Get unified profile strength score and breakdown
 * @param {string} clientId
 * @returns {Promise<Object|null>}
 */
export async function getProfileStrength(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/strength`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });

  if (!response.ok) {
    return null;
  }

  const json = await response.json();
  return json?.strength ?? null;
}

export async function getReadiness(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/readiness`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Readiness check failed');
  }
  return json;
}

/**
 * Run profile generation (long-running 2-3 min)
 * @param {string} clientId 
 * @returns {Promise<{success: boolean, auditId: string, runId: string, score: number, tokensUsed: number, latencyMs: number, message: string}>}
 */
export async function runGeneration(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/run`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: '{}'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Generation run failed');
  }
  return json;
}

/**
 * Get profile preview (markdown and HTML)
 * @param {string} clientId 
 * @returns {Promise<{markdown: string, html: string, generated: boolean, updated_at: string}>}
 */
export async function getPreview(clientId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/preview`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Preview fetch failed');
  }
  return json.data;
}

/**
 * Get audit history for profile generations
 * @param {string} clientId 
 * @param {number} limit - Max number of records (default 10)
 * @returns {Promise<Array<{auditId: string, timestamp: string, model: string, tokensUsed: number, latencyMs: number, readinessScore: number, success: boolean}>>}
 */
export async function getAuditHistory(clientId, limit = 10) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/audits?limit=${limit}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to load audit history');
  }
  return json.data || [];
}

/**
 * Get real-time status of a running profile generation
 * Phase 4: Poll this every 2-3 seconds to get progress updates
 * @param {string} clientId 
 * @param {string} auditId 
 * @returns {Promise<{success: boolean, stage: string, progressPct: number, message: string, ...}>}
 */
export async function getRunStatus(clientId, auditId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/run/${auditId}/status`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Status check failed');
  }
  return json;
}

/**
 * Request cancellation of a running profile generation
 * Phase 4: Cooperative cancellation - stops at next checkpoint
 * @param {string} clientId 
 * @param {string} auditId 
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function cancelRun(clientId, auditId) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}/run/${auditId}/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: '{}'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Cancel failed');
  }
  return json;
}

// ============================================================================
// PHASE 5: VALIDATION SERVICES
// ============================================================================

/**
 * Validate NPI against NPPES registry
 * Phase 5: Real-time NPI validation with organization name lookup
 * @param {string} npi - 10-digit NPI number
 * @returns {Promise<{valid: boolean, name?: string, entityType?: string}>}
 */
export async function validateNpi(npi) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/validate/npi/${encodeURIComponent(npi)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'NPI validation failed');
  }
  return json.data; // { valid: bool, name?: string, entityType?: 'organization'|'individual' }
}

/**
 * Validate and normalize address
 * Phase 5: Address validation with Google Maps API fallback
 * @param {string} addressQuery - Full address string
 * @returns {Promise<{valid: boolean, street?: string, city?: string, state?: string, zip?: string, normalized?: string}>}
 */
export async function validateAddress(addressQuery) {
  const token = localStorage.getItem('dva_token');
  const response = await fetch(`${BASE}/api/v2/client-profile/validate/address?q=${encodeURIComponent(addressQuery)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Address validation failed');
  }
  return json.data; // { valid: bool, street, city, state, zip, normalized?: string }
}

// ============================================================================
// PHASE 6: TEMPLATE/DEFAULTS SERVICES
// ============================================================================

/**
 * Get organization type defaults from field registry
 * Phase 6: Auto-apply defaults based on organization type selection
 * @param {string} orgType - Organization type (e.g., "Health System", "Hospital")
 * @param {string} variant - Optional variant (e.g., "small", "large") - for future use
 * @returns {Promise<Object>} - Defaults object { beds: 5000, service_lines: [...], ... }
 */
export async function getOrgDefaults(orgType, variant = null) {
  const token = localStorage.getItem('dva_token');
  const url = `${BASE}/api/v2/client-profile/template/${encodeURIComponent(orgType)}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
  
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include'
  });
  
  const json = await response.json();
  
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to load organization defaults');
  }
  
  return json.data || {}; // Returns flat object: { beds: 5000, service_lines: [...], ... }
}

/**
 * Sync alternate API/form field names so values round-trip correctly.
 * The profile form uses *_pct keys while the API may store *_percentage keys.
 *
 * Organization Type is stored by the API as client_type_id (and derived
 * organization_type / organization_type_code on read). Sending only
 * organization_type on save is ignored — we must also send client_type_id.
 */
/**
 * Organization Type catalog (mirrors backend client_types).
 * `selectable: false` hides the type from the org-profile dropdown without
 * removing it from the catalog/DB so existing orgs still resolve and the type
 * can be re-enabled later (DMD-1183).
 */
const ORG_TYPE_BY_ID = {
  1: { label: 'Health System', code: 'health_system', selectable: true },
  2: { label: 'Hospital', code: 'hospital', selectable: true },
  3: { label: 'Collaboration', code: 'collaboration', selectable: true },
  4: { label: 'Healthcare Consultant', code: 'consultant', selectable: false },
  5: { label: 'Digital Health Vendor', code: 'vendor', selectable: false },
};

const ORG_TYPE_BY_LABEL = Object.fromEntries(
  Object.entries(ORG_TYPE_BY_ID).map(([id, meta]) => [meta.label, { id: Number(id), code: meta.code, selectable: meta.selectable !== false }])
);

const ORG_TYPE_BY_CODE = Object.fromEntries(
  Object.entries(ORG_TYPE_BY_ID).map(([id, meta]) => [meta.code, { id: Number(id), label: meta.label, selectable: meta.selectable !== false }])
);

/** Labels offered in the Organisation Type dropdown (excludes temporarily hidden types). */
export function getSelectableOrganizationTypes() {
  return Object.values(ORG_TYPE_BY_ID)
    .filter((meta) => meta.selectable !== false)
    .map((meta) => meta.label);
}

/**
 * Dropdown options for org profile. Includes the current value even when that
 * type is hidden, so existing consultant/vendor orgs are not blanked out.
 */
export function getOrganizationTypeDropdownOptions(currentType) {
  const options = getSelectableOrganizationTypes();
  if (
    currentType
    && !options.includes(currentType)
    && ORG_TYPE_BY_LABEL[currentType]
  ) {
    return [...options, currentType];
  }
  return options;
}

function syncOrganizationTypeFields(profile) {
  let label = profile.organization_type || '';
  let code = profile.organization_type_code || '';
  let typeId =
    profile.client_type_id != null && profile.client_type_id !== ''
      ? Number(profile.client_type_id)
      : null;

  if (!label && code && ORG_TYPE_BY_CODE[code]) {
    label = ORG_TYPE_BY_CODE[code].label;
    typeId = Number.isFinite(typeId) ? typeId : ORG_TYPE_BY_CODE[code].id;
  }

  if (!label && Number.isFinite(typeId) && ORG_TYPE_BY_ID[typeId]) {
    label = ORG_TYPE_BY_ID[typeId].label;
    code = code || ORG_TYPE_BY_ID[typeId].code;
  }

  if (label && ORG_TYPE_BY_LABEL[label]) {
    code = ORG_TYPE_BY_LABEL[label].code;
    typeId = ORG_TYPE_BY_LABEL[label].id;
  }

  if (label) profile.organization_type = label;
  if (code) profile.organization_type_code = code;
  if (Number.isFinite(typeId)) profile.client_type_id = typeId;

  return profile;
}

function syncProfileFieldAliases(profile) {
  const pairs = [
    ['organization_name', 'client_name'],
    ['beds', 'total_beds'],
    ['lives_under_vbc_agreements', 'lives_under_vbc'],
    ['rural_pct', 'rural_percentage'],
    ['urban_pct', 'urban_percentage'],
    ['suburban_pct', 'suburban_percentage'],
  ];

  for (const [primary, alternate] of pairs) {
    const primaryValue = profile[primary];
    const alternateValue = profile[alternate];
    const hasPrimary = primaryValue !== null && primaryValue !== undefined && primaryValue !== '';
    const hasAlternate = alternateValue !== null && alternateValue !== undefined && alternateValue !== '';

    if (hasPrimary && !hasAlternate) {
      profile[alternate] = primaryValue;
    } else if (hasAlternate && !hasPrimary) {
      profile[primary] = alternateValue;
    }
  }

  return syncOrganizationTypeFields(profile);
}

/**
 * Keep organization_name and client_name in sync — the API stores the
 * canonical name on the clients row as client_name while the profile form
 * edits organization_name.
 */
export function normalizeClientProfile(profile) {
  if (!profile) return profile;
  const name = profile.organization_name || profile.client_name || '';
  return syncProfileFieldAliases({
    ...profile,
    organization_name: name,
    client_name: name,
  });
}

/**
 * Get complete client profile data
 * @param {string} clientId 
 * @returns {Promise<Object>} - Complete profile object
 */
const PROFILE_TTL_MS = 60_000;
const PROFILE_STORE_KEY = '__dvaClientProfileCache';

function profileStores() {
  const g = globalThis;
  if (!g[PROFILE_STORE_KEY]) {
    g[PROFILE_STORE_KEY] = { inflight: new Map(), cache: new Map() };
  }
  return g[PROFILE_STORE_KEY];
}

export async function getClientProfile(clientId) {
  const cacheKey = String(clientId);
  const { inflight: profileInflight, cache: profileCache } = profileStores();
  const cached = profileCache.get(cacheKey);
  if (cached && Date.now() - cached.at < PROFILE_TTL_MS) {
    return cached.value;
  }

  const existing = profileInflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    const token = localStorage.getItem('dva_token');
    const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to load client profile: ${response.statusText}`);
    }

    const json = await response.json();

    // Handle different response formats
    let value;
    if (json?.success && json?.data) {
      value = normalizeClientProfile(json.data);
    } else if (json?.client_id || json?.organization_name || json?.client_name) {
      value = normalizeClientProfile(json);
    } else {
      throw new Error('Invalid profile response format');
    }
    profileCache.set(cacheKey, { at: Date.now(), value });
    return value;
  })().finally(() => {
    if (profileInflight.get(cacheKey) === promise) profileInflight.delete(cacheKey);
  });

  profileInflight.set(cacheKey, promise);
  return promise;
}

/**
 * Save client profile data
 * @param {string} clientId
 * @param {Object} profile
 * @returns {Promise<Object>} - Saved profile object
 */
export async function saveClientProfile(clientId, profile) {
  const { inflight: profileInflight, cache: profileCache } = profileStores();
  profileCache.delete(String(clientId));
  profileInflight.delete(String(clientId));
  const token = localStorage.getItem('dva_token');
  const payload = normalizeClientProfile(profile);
  const response = await fetch(`${BASE}/api/v2/client-profile/${clientId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json();

  if (!response.ok || !json?.success) {
    throw new Error(json?.error || `Failed to save profile: ${response.statusText}`);
  }

  // POST response often omits derived fields like organization_type — merge with
  // the payload we sent so the UI does not clear Organization Type after save.
  const responseData = json.data && typeof json.data === 'object' ? json.data : {};
  return normalizeClientProfile({
    ...payload,
    ...responseData,
    organization_type: responseData.organization_type || payload.organization_type,
    organization_type_code: responseData.organization_type_code || payload.organization_type_code,
    client_type_id: responseData.client_type_id ?? payload.client_type_id,
  });
}